import React, { useEffect, useState } from 'react';
import { Offcanvas, OffcanvasHeader, OffcanvasBody } from 'reactstrap';
import { toast } from 'react-toastify';
import { Attivita } from '../../../Types/Panel.type';
import { postJson, get, putJson, del } from '../../../hook/request';
import { Btn } from '../../../AbstractElements';

type Commenti = {
    ID: number;
    attivitaID: number;
    userID: number;
    commento: string;
    cognome: string;
    nome: string;
    email: string;
    creato: 1 | 0;
};

type CommentoTask = {
    ID: number;
    attivitaID: number;
    descrizione: string;
    descCommento: string | null;
};

type CommentiResponse = {
    commenti_attivita?: Commenti[];
    commenti_task?: CommentoTask[];
};

type Props = {
    open: boolean;
    onClose: () => void;
    attivita: Attivita | null;
    onTasksSaved?: (attivitaId: number) => void;
};

const AttivitaTaskDrawer: React.FC<Props> = ({ open, onClose, attivita }) => {
    const [commento, setCommento] = useState('');
    const [listCommento, setListCommenti] = useState<Commenti[]>([]);
    const [listCommentiTask, setListCommentiTask] = useState<CommentoTask[]>([]);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingText, setEditingText] = useState('');


    const handleCommenti = async () => {
        if (!attivita?.ID) return;

        try {
            const res = await get<CommentiResponse>(
                `/progetti/commenti?attivitaID=${attivita.ID}`
            );
            const payload = res.data;
            setListCommenti(
                Array.isArray(payload?.commenti_attivita)
                    ? payload.commenti_attivita
                    : []
            );
            setListCommentiTask(
                Array.isArray(payload?.commenti_task)
                    ? payload.commenti_task
                    : []
            );
        } catch (err) {
            console.error('Errore caricamento commenti', err);
            toast.error('Errore nel caricamento dei commenti.');
        }
    };


    useEffect(() => {
        if (open && attivita?.ID) {
            handleCommenti();
        }
    }, [open, attivita?.ID]);

    async function handleAddComment() {
        if (!attivita?.ID) {
            toast.error('Nessuna attività selezionata per il commento.');
            return;
        }
        if (!commento.trim()) {
            toast.warn('Il commento è vuoto');
            return;
        }

        const payload = {
            attivita: attivita.ID,
            commento,
        };

        try {
            await postJson('/progetti/commenti', payload);
            toast.success('Commento aggiunto con successo');
            setCommento('');
            await handleCommenti();
        } catch (err) {
            console.error('Errore salvataggio commento', err);
            toast.error('Errore nel salvataggio del commento.');
        }
    }


    function startEditComment(c: Commenti) {
        setEditingId(c.ID);
        setEditingText(c.commento);
    }

    function cancelEdit() {
        setEditingId(null);
        setEditingText('');
    }

    async function saveEdit() {
        if (editingId == null) return;
        if (!editingText.trim()) {
            toast.warn('Il commento è vuoto');
            return;
        }

        try {
            await putJson(`/progetti/commenti?ID=${editingId}`, {
                commento: editingText,
            });
            toast.success('Commento aggiornato con successo');
            setEditingId(null);
            setEditingText('');
            await handleCommenti(); // ricarica la lista
        } catch (err) {
            console.error('Errore aggiornamento commento', err);
            toast.error('Errore nell’aggiornamento del commento.');
        }
    }

    async function handleDeleteComment(id: number) {
        if (!window.confirm('Vuoi davvero eliminare questo commento?')) return;

        try {
            await del(`/progetti/commenti?ID=${id}`);
            toast.success('Commento eliminato');
            await handleCommenti();
        } catch (err) {
            console.error('Errore eliminazione commento', err);
            toast.error('Errore nell’eliminazione del commento.');
        }
    }


    function handleClose() {
        onClose();
    }

    return (
        <Offcanvas
            isOpen={open}
            toggle={handleClose}
            direction="start"
            style={{ width: 450 }}
        >
            <OffcanvasHeader toggle={handleClose}>
                Commenti attività {attivita?.ID ?? ''}
            </OffcanvasHeader>

            <OffcanvasBody>
                <div className="mb-3">
                    <textarea
                        className="w-100 rounded-2 p-2"
                        rows={5}
                        value={commento}
                        onChange={e => setCommento(e.target.value)}
                    />
                </div>

                <Btn color="primary" onClick={handleAddComment}>
                    Aggiungi Commento
                </Btn>

                <hr />

                <div>
                    {listCommento.map(c => {
                        const isEditing = editingId === c.ID;
                        return (
                            <div key={c.ID} className="mb-2 border rounded p-2">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="small text-muted">
                                        {c.nome} {c.cognome} ({c.email})
                                    </div>

                                    {c.creato === 1 && (
                                        <div className="d-flex gap-2">
                                            {!isEditing && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => startEditComment(c)}
                                                    >
                                                        <i className="fa-solid fa-pen" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDeleteComment(c.ID)}
                                                    >
                                                        <i className="fa-solid fa-trash" />
                                                    </button>
                                                </>
                                            )}

                                            {isEditing && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-success"
                                                        onClick={saveEdit}
                                                    >
                                                        <i className="fa-solid fa-check" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={cancelEdit}
                                                    >
                                                        <i className="fa-solid fa-xmark" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="mt-2">
                                    {isEditing ? (
                                        <textarea
                                            className="form-control form-control-sm"
                                            rows={3}
                                            value={editingText}
                                            onChange={e => setEditingText(e.target.value)}
                                        />
                                    ) : (
                                        <div>{c.commento}</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {listCommentiTask.length > 0 && (
                    <>
                        <hr />
                        <h6 className='mb-2'>Commenti task</h6>

                        <div>
                            {listCommentiTask.map(task => (
                                <div
                                    key={task.ID}
                                    className='mb-2 border rounded p-2'
                                >
                                    <div className='small fw-semibold mb-1'>
                                        Task #{task.ID}
                                    </div>
                                    <div className='small text-muted mb-2'>
                                        {task.descrizione}
                                    </div>
                                    <div>{task.descCommento}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default AttivitaTaskDrawer;
